import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { useLocalListSearch } from '@/hooks/useLocalListSearch';
import { limitTypesQueryOptions } from '../api/limitTypes.api';
import { useLimitTypeActionModals } from '../hooks/useLimitTypeActionModals';
import type { LimitType } from '../types/limitType.types';
import { getLimitTypeColumns } from './LimitTypeColumns';
import { LimitTypeFormModal } from './LimitTypeFormModal';

const route = getRouteApi('/_auth/settings/limit-types');

const toSearchableValues = (limitType: LimitType) => [limitType.name];

export function LimitTypeListTable() {
  const { q: appliedSearchText } = route.useSearch();
  const navigate = route.useNavigate();

  const { limitTypeToEdit, openEditModal, closeModal } = useLimitTypeActionModals();

  const { data, isLoading, error, refetch } = useQuery(limitTypesQueryOptions());

  const { searchText, setSearchText, visibleRows } = useLocalListSearch({
    rows: data,
    appliedText: appliedSearchText,
    onApply: (text) => void navigate({ search: { q: text }, replace: true }),
    toSearchableValues,
  });

  const columns = useMemo(
    () => getLimitTypeColumns(appliedSearchText, { onEdit: openEditModal }),
    [appliedSearchText, openEditModal],
  );

  return (
    <>
      <div className={FIT_TO_SCREEN_PAGE_CLASS}>
        <PageHeader
          title="Limit Types"
          search={{
            value: searchText,
            onChange: setSearchText,
            placeholder: 'Search by limit type',
          }}
        />

        {error ? (
          <ErrorState
            error={error}
            fallbackTitle="Could not load limit types"
            onRetry={() => void refetch()}
            fill
          />
        ) : (
          <DataTable
            data={visibleRows}
            columns={columns}
            isLoading={isLoading}
            stickyColumnId="name"
            className="lg:flex-1"
          />
        )}
      </div>

      <LimitTypeFormModal
        limitType={limitTypeToEdit}
        open={limitTypeToEdit !== null}
        onOpenChange={(open) => !open && closeModal()}
      />
    </>
  );
}
